import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CircleAlert, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, getErrorMessage } from "@/lib/api";

// THE single Forward Lead implementation, used everywhere in the app.
// Fields: Target company, Reason, Remarks — no job field (receiver decides).
// Modes:
//   - fixed application:  <ForwardLeadDialog application={{ id, label }} ... />
//   - picker (standalone Forward Leads page): omit `application`; an eligible-
//     applications selector is shown on top. Same fields, same endpoint,
//     same behavior in both modes.
// Load failures are ALWAYS visible (toast + inline alert with Retry) — never
// silently swallowed (regression guard for the limit-mismatch bug).

export const ForwardLeadDialog = ({ open, onOpenChange, application = null, onForwarded }) => {
  const pickerMode = !application;
  const [companies, setCompanies] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [eligibleApps, setEligibleApps] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ application_id: "", company_id: "", reason: "", remarks: "" });

  const loadOptions = useCallback(() => {
    setLoadError(null);
    setLoading(true);
    const calls = [
      api.get("/companies", { params: { limit: 500 } }),
      api.get("/forward-leads/options"),
    ];
    if (pickerMode) calls.push(api.get("/forward-leads/eligible-applications"));
    Promise.all(calls)
      .then(([c, o, e]) => {
        setCompanies(c.data.items || c.data || []);
        setReasons(o.data.reasons || []);
        if (pickerMode) setEligibleApps(e.data || []);
      })
      .catch((err) => {
        const msg = getErrorMessage(err);
        setLoadError(msg);
        toast.error(`Could not load Forward Lead options: ${msg}`);
      })
      .finally(() => setLoading(false));
  }, [pickerMode]);

  useEffect(() => {
    if (open) {
      setForm({ application_id: "", company_id: "", reason: "", remarks: "" });
      loadOptions();
    }
  }, [open, loadOptions]);

  const submit = async () => {
    const appId = application?.id || form.application_id;
    if (!appId) return toast.error("Choose the candidate / application");
    if (!form.company_id) return toast.error("Choose the target company");
    if (!form.reason) return toast.error("Choose the forward reason");
    setBusy(true);
    try {
      const res = await api.post(`/recruiter-flow/applications/${appId}/forward-lead`, {
        company_id: form.company_id, reason: form.reason, remarks: form.remarks || undefined,
      });
      const lead = res.data.lead || {};
      toast.success(lead.status === "Awaiting Allocation"
        ? `${lead.fwd_code || "Lead"} created — no allocated recruiters yet, admin will assign`
        : `Lead forwarded (${lead.fwd_code || "created"}) — track it under Forward Leads`);
      onOpenChange(false);
      onForwarded && onForwarded(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-xl max-h-[90vh] overflow-y-auto" data-testid="forward-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            Forward Lead{application?.label ? ` — ${application.label}` : ""}
          </DialogTitle>
          <DialogDescription>
            Recruiters allocated to the target company will see a masked preview until one of them
            accepts. The working slot is freed immediately.
          </DialogDescription>
        </DialogHeader>

        {loadError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-2" data-testid="forward-load-error">
            <p className="text-sm text-red-700 flex items-start gap-2">
              <CircleAlert className="h-4 w-4 mt-0.5 shrink-0" />
              Could not load options: {loadError}
            </p>
            <Button variant="outline" size="sm" className="rounded-lg bg-white" onClick={loadOptions} data-testid="forward-retry-btn">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
            </Button>
          </div>
        ) : loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center" data-testid="forward-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading options...
          </div>
        ) : (
          <div className="space-y-4">
            {pickerMode && (
              <div className="space-y-1.5">
                <Label>Candidate / application<span className="text-destructive ml-0.5">*</span></Label>
                <Select value={form.application_id} onValueChange={(v) => setForm({ ...form, application_id: v })}>
                  <SelectTrigger className="bg-white" data-testid="fwd-app-select"><SelectValue placeholder="Choose your candidate" /></SelectTrigger>
                  <SelectContent>
                    {eligibleApps.length === 0 && <p className="text-sm text-muted-foreground p-3">No active applications available.</p>}
                    {eligibleApps.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.candidate_name} · {a.app_code}{a.job_title ? ` · ${a.job_title}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Target company<span className="text-destructive ml-0.5">*</span></Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger className="bg-white" data-testid="fwd-company-select"><SelectValue placeholder="Choose company..." /></SelectTrigger>
                <SelectContent>
                  {companies.length === 0 && <p className="text-sm text-muted-foreground p-3">No companies available.</p>}
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Reason<span className="text-destructive ml-0.5">*</span></Label>
              <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                <SelectTrigger className="bg-white" data-testid="fwd-reason-select"><SelectValue placeholder="Choose reason..." /></SelectTrigger>
                <SelectContent>
                  {reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Remarks (optional)</Label>
              <Textarea rows={2} className="bg-white" data-testid="fwd-remarks-input" value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                placeholder="Context for the accepting recruiter..." />
            </div>
            <Button onClick={submit} disabled={busy} className="w-full rounded-lg" data-testid="fwd-submit-btn">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Forward Lead
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
